import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "@/locales/en.json";
import ru from "@/locales/ru.json";

export const DEFAULT_LANGUAGE = "ru";
export const SUPPORTED_LANGUAGES = ["ru", "en"] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const normalizeLanguage = (lng?: string | null): SupportedLanguage => {
    if (!lng) return DEFAULT_LANGUAGE;
    const normalized = lng.toLowerCase().split("-")[0];
    return SUPPORTED_LANGUAGES.includes(normalized as SupportedLanguage)
        ? (normalized as SupportedLanguage)
        : DEFAULT_LANGUAGE;
};

const parseSupportedLanguage = (lng?: string | null): SupportedLanguage | null => {
    if (!lng) return null;

    const normalized = lng.toLowerCase().split("-")[0];
    return SUPPORTED_LANGUAGES.includes(normalized as SupportedLanguage)
        ? (normalized as SupportedLanguage)
        : null;
};

export const getBrowserLanguage = (): SupportedLanguage => {
    if (typeof window === "undefined") return DEFAULT_LANGUAGE;
    return normalizeLanguage(window.navigator.language);
};

const getStoredLanguage = (): SupportedLanguage | null => {
    if (typeof window === "undefined") return null;

    try {
        const raw = window.localStorage.getItem("interfaceLanguage");
        return parseSupportedLanguage(raw);
    } catch {
        return null;
    }
};

type InitI18nBoardData = {
    interfaceLanguage?: string;
    token?: string;
    temporary?: boolean;
};

export const initI18n = (boardData?: InitI18nBoardData) => {
    if (i18n.isInitialized) return i18n;
    const preferredLanguage = boardData?.interfaceLanguage;
    const backendLanguage = boardData?.temporary ? null : parseSupportedLanguage(preferredLanguage);
    const localLanguage = getStoredLanguage();
    const browserLanguage = getBrowserLanguage();
    const resolvedLanguage =
        backendLanguage ?? localLanguage ?? browserLanguage ?? DEFAULT_LANGUAGE;

    i18n.use(initReactI18next).init({
        resources: {
            ru: { translation: ru },
            en: { translation: en },
        },
        lng: resolvedLanguage,
        fallbackLng: DEFAULT_LANGUAGE,
        interpolation: {
            escapeValue: false,
        },
        returnEmptyString: false,
    });

    return i18n;
};

export const t = (key: string, options?: Record<string, unknown>) => i18n.t(key, options);

export default i18n;
